import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useChartDashboard } from './ChartDashboard/useChartDashboard';
import { useInventoryDashboard } from './InventoryDashboard/useInventoryDashboard';
import { usePricingDashboard } from './PricingDashboard/usePricingDashboard';

interface ReportsDashboardRefreshContextProps {
  refreshDashboardQueries: () => void;
}

const Context = createContext<ReportsDashboardRefreshContextProps | undefined>(undefined);

/**
 * Refreshes all dashboard queries (Pricing, Inventory, Chart).
 * Does not refresh Packhouse and ReportDashboardsTabs.
 */
export const ReportsDashboardRefreshProvider = ({ children }: { children: ReactNode }) => {
  const { refreshPricingDashboardNewResult } = usePricingDashboard();
  const { refreshInventoryDashboardNewResult } = useInventoryDashboard();
  const { refreshChartDashboardNewResult } = useChartDashboard();

  const refreshDashboardQueries = useCallback(() => {
    refreshPricingDashboardNewResult();
    refreshInventoryDashboardNewResult();
    refreshChartDashboardNewResult();
  }, [refreshPricingDashboardNewResult, refreshInventoryDashboardNewResult, refreshChartDashboardNewResult]);

  const value = useMemo(() => ({ refreshDashboardQueries }), [refreshDashboardQueries]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useReportsDashboardRefresh = () => {
  const result = useContext(Context);
  if (!result) {
    throw new Error('useReportsDashboardRefresh must be used within ReportsDashboardRefreshProvider');
  }
  return result;
};
